<script lang="ts">
  import { buildTableRowActionSet } from "@kontextwerk/web-sdk/core/layout/table"
  import { onDestroy } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import StatusTag from "@kontextwerk/web-sdk/ui/display/StatusTag.svelte"
  import Table, {
    type ColumnDef,
    type DataRow,
    type TableFilterValues,
    type TableGetRows,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import { getSortedTablePage } from "@kontextwerk/web-sdk/core/layout/tableSort"
  import { closeOverlayById, openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { mdiEyeOutline } from "@mdi/js"
  import type { OrganizationFrame } from "@kontextwerk/control-web-client"
  import type { PermissionState } from "@kontextwerk/control-web-frame/shared/logic/authorization"
  import { kehrblattFrameMembers } from "../../../../../../../../../../api/kehrblatt/organization-frame"
  import type { UsersDirectory } from "../../../../_shared/_logic/users-directory"
  import { countMembersByRole } from "../../../../_shared/_logic/role-presentation"
  import RoleOverlay from "./_modules/role-overlay"
  import {
    ROLE_COLUMNS,
    buildRoleRows,
    filterRoleRows,
    roleRowsSignature,
    type RoleRow,
  } from "./_logic/table"

  interface Props {
    organization: OrganizationFrame
    directory: UsersDirectory
    permissions: PermissionState
  }

  let { organization, directory, permissions }: Props = $props()

  const canManage = $derived(permissions.allows("core.roles", "manage"))
  const rows = $derived(
    buildRoleRows(directory.roles, countMembersByRole(kehrblattFrameMembers(organization))),
  )
  const refreshKey = $derived(roleRowsSignature(rows))
  let overlayId: string | null = null

  const getRows: TableGetRows<TableFilterValues> = async (params) => {
    const { filters, limit, offset } = params
    const sort =
      (params as { sort?: { columnId: string; key: string; dir: "asc" | "desc" } | null }).sort ??
      null
    await directory.load()

    return getSortedTablePage({
      rows: filterRoleRows(rows, String(filters?.name ?? "")),
      columns: ROLE_COLUMNS,
      sort,
      limit,
      offset,
    })
  }

  const closeRoleOverlay = () => {
    if (overlayId) closeOverlayById(overlayId)

    overlayId = null
  }

  const openRole = (row: RoleRow) => {
    const access = directory.access

    if (!access || !directory.current()) return

    closeRoleOverlay()
    let guard: (() => boolean | Promise<boolean>) | null = null
    const id = openOverlay({
      title: row.name,
      content: RoleOverlay,
      properties: {
        roleId: row.roleId,
        access,
        canManage,
        permissions,
        actorRoleId: directory.actor?.roleId ?? null,
        isCurrent: directory.current,
        onSaved: () => directory.reloadRoles(),
        setBeforeClose: (next: typeof guard) => {
          guard = next
        },
      },
      beforeClose: () => guard?.() ?? true,
      onClose: () => {
        if (overlayId === id) overlayId = null
      },
    })
    overlayId = id
  }

  // effect-ok: external-runtime - a confirmed-context change closes this owner's role dialog.
  $effect(() => {
    if (!directory.available) closeRoleOverlay()
  })

  onDestroy(closeRoleOverlay)
</script>

{#snippet rolesTableCell({ row, column }: { row: DataRow; column: ColumnDef })}
  {@const roleRow = row as RoleRow}
  {#if column.id === "kindLabel"}
    <StatusTag
      variant="neutral"
      size="sm"
      status={{ value: roleRow.kind, label: roleRow.kindLabel }}
    />
  {:else if column.id === "members"}
    {roleRow.members}
  {:else}
    {roleRow.name}
  {/if}
{/snippet}

{#snippet rolesEmpty()}
  <p class="hint">Keine Rollen vorhanden.</p>
{/snippet}

<div class="roles-table" data-testid="controlAdmin.roles.table">
  {#if directory.rolesState === "failed"}
    <div class="state-row" role="alert">
      <p class="hint">Rollen konnten nicht geladen werden.</p>
      <CTA
        type="secondaryButton"
        content="Erneut versuchen"
        onClick={() => void directory.reloadRoles()}
      />
    </div>
  {/if}
  <Table
    titleRow={{ content: "Rollen", brightBackground: true }}
    columns={ROLE_COLUMNS}
    {getRows}
    getRowId={(row) => String(row.id)}
    urlStateId={`org-roles-${organization.id}`}
    {refreshKey}
    loading={directory.rolesState === "loading"}
    theme="light"
    mode="table"
    showActions={true}
    actionsHeaderLabel="Aktionen"
    filter={{ column: "name", type: "text", label: "Nach Rolle filtern…" }}
    tableCell={rolesTableCell}
    rowActions={{
      resolve: (row) =>
        buildTableRowActionSet(
          [
            {
              id: "open-role",
              label: "Rolle öffnen",
              icon: mdiEyeOutline,
              tooltip:
                canManage && (row as RoleRow).kind === "custom"
                  ? "Rolle öffnen und Berechtigungen bearbeiten"
                  : "Rolle und Berechtigungen ansehen",
              onClick: () => openRole(row as RoleRow),
            },
          ],
          { primaryId: "open-role" },
        ),
    }}
    empty={rolesEmpty}
  />
</div>

<style lang="less">
  .roles-table {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    min-width: 0;
  }

  .state-row {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    flex-wrap: wrap;
  }

  .hint {
    margin: 0;
    color: var(--text-invers-100);
  }
</style>
